import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  getGlobalConfig,
  saveGlobalConfig,
  normalizeCalendar,
  DEFAULT_GLOBAL_CONFIG,
  GLOBAL_CONFIG_KEYS,
} from "../../services/backend/AppConfigService";
import InstitutionalCalendar from "./InstitutionalCalendar";

const pick = (obj, keys) => Object.fromEntries(keys.filter((k) => k in obj).map((k) => [k, obj[k]]));

// App-wide settings (institution name, brand, portal base URL, logo). Stored in
// config/global and reused by every module (PQRSD, Extensión, …). ADMIN-only,
// since the config module itself is gated by PERMISSIONS.CONFIG.LIST.
export default function GlobalSettings() {
  const [cfg, setCfg] = useState(DEFAULT_GLOBAL_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    (async () => {
      const data = await getGlobalConfig();
      setCfg({ ...DEFAULT_GLOBAL_CONFIG, ...pick(data, GLOBAL_CONFIG_KEYS) });
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    const color = (cfg.brandColor || "").trim();
    const logo = (cfg.logoUrl || "").trim();
    const lw = Number(cfg.logoWidth) || DEFAULT_GLOBAL_CONFIG.logoWidth;
    const patch = {
      orgName: (cfg.orgName || "").trim(),
      brandName: (cfg.brandName || "").trim(),
      brandColor: /^#[0-9a-fA-F]{6}$/.test(color) ? color : "",
      publicBaseUrl: (cfg.publicBaseUrl || "").trim(),
      logoUrl: /^https?:\/\//i.test(logo) ? logo : "",
      logoWidth: Math.min(Math.max(lw, 40), 320),
      calendar: normalizeCalendar(cfg.calendar),
    };
    const ok = await saveGlobalConfig(patch);
    setSaving(false);
    setFeedback(ok
      ? { severity: "success", msg: "Ajustes generales guardados." }
      : { severity: "error", msg: "No fue posible guardar. Intenta de nuevo." });
  };

  if (loading) return <Typography sx={{ p: 3 }}>Cargando…</Typography>;

  const previewColor = /^#[0-9a-fA-F]{6}$/.test(cfg.brandColor || "") ? cfg.brandColor : "#00652e";

  return (
    <Box sx={{ mt: 2 }}>
      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Ajustes generales</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configuración compartida por toda la plataforma: nombre de la institución, marca de los
            correos y dominio de los enlaces públicos.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Nombre de la institución" value={cfg.orgName || ""}
                onChange={(e) => set("orgName", e.target.value)}
                placeholder="p. ej. Institución Universitaria Tecnológico de Antioquia"
                helperText="Se muestra en la postulación pública (autorización de datos), la página de inicio y los correos. En blanco = valor por defecto."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Nombre en los correos" value={cfg.brandName || ""}
                onChange={(e) => set("brandName", e.target.value)}
                placeholder="p. ej. TdeA Extensión"
                helperText="Aparece como remitente y en el encabezado/pie. En blanco = valor por defecto."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  label="Color de marca" value={cfg.brandColor || ""}
                  onChange={(e) => set("brandColor", e.target.value)}
                  placeholder="#00652e"
                  helperText="Color base de los correos."
                  sx={{ flex: 1 }}
                />
                <Box
                  component="input" type="color" value={previewColor}
                  onChange={(e) => set("brandColor", e.target.value)}
                  sx={{ width: 48, height: 48, p: 0, border: "1px solid #ccc", borderRadius: 1, cursor: "pointer", bgcolor: "transparent" }}
                  title="Elegir color"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="URL base del portal" value={cfg.publicBaseUrl || ""}
                onChange={(e) => set("publicBaseUrl", e.target.value)}
                placeholder="https://archivo.tdea.edu.co"
                helperText="Dominio de los enlaces de correo (magic links de postulación, QR). En blanco = se deriva automáticamente."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Logo (URL externa)" value={cfg.logoUrl || ""}
                onChange={(e) => set("logoUrl", e.target.value)}
                placeholder="https://…/logo.png"
                helperText="Enlace https a la imagen del logo. En blanco = solo el nombre."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tamaño del logo: {cfg.logoWidth || 140} px
              </Typography>
              <Slider
                value={Number(cfg.logoWidth) || 140}
                min={40} max={320} step={4} valueLabelDisplay="auto" disabled={!cfg.logoUrl}
                onChange={(_, v) => set("logoWidth", v)}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? "Guardando…" : "Guardar ajustes"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Working calendar. Lives here rather than inside PQRSD because every
          module that counts business days has to agree with it. */}
      <InstitutionalCalendar
        calendar={cfg.calendar || DEFAULT_GLOBAL_CONFIG.calendar}
        onChange={(calendar) => set("calendar", calendar)}
      />

      {/* Simple brand preview (module-agnostic). PQRSD keeps its own email preview. */}
      <Card variant="outlined">
        <Box sx={{ bgcolor: previewColor, px: 3, py: 2, display: "flex", alignItems: "center", gap: 2 }}>
          {cfg.logoUrl
            ? <Box component="img" src={cfg.logoUrl} alt="" sx={{ width: Number(cfg.logoWidth) || 140, maxHeight: 60, objectFit: "contain" }} />
            : <Typography variant="h6" sx={{ color: "#fff" }}>{cfg.brandName || cfg.orgName || "Marca"}</Typography>}
        </Box>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            Vista previa de la marca. Así se ve el encabezado con el color y logo actuales.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
